/**
 * The gatherable-node ground ring (sprint-assembly-1, AD-647). A resource node shares its
 * silhouette with the scenery around it — a `node-flora` timber stand is a tree among
 * scatter trees — and the owner's playtest could not find one. A thin pale-gold ring on
 * the ground under every node says "this one can be gathered" without a new mesh, a
 * texture or a shader. Sized per archetype from the kit's measured footprints (flora
 * 2.4 m, mineral 2.1 m, salvage 1.0 m, herb 0.7 m wide).
 *
 * Presentation only; injected into `NodePlacer` as `makeHighlight` so the placer stays
 * node-safe. Clear of the Bible §3.7 reserved hues (telegraph red, heal green) — a red
 * ring on the ground would read as an attack telegraph, which is exactly the confusion
 * this colour avoids.
 */

import * as THREE from 'three';

/** Ring colour (sRGB): pale gold, the same family as the questgiver marker. */
export const NODE_RING_COLOUR = '#f3e3b0';

/** Ring radius (metres) per node archetype; unresolved nodes (placeholders) use `default`. */
export const NODE_RING_RADIUS: Readonly<Record<string, number>> = {
  'node-flora': 1.5,
  'node-mineral': 1.35,
  'node-salvage': 0.8,
  'node-herb': 0.6,
  default: 0.8,
};

/** Ring band width (metres) and its lift off the ground (avoids z-fighting with it). */
const RING_WIDTH = 0.1;
const RING_LIFT = 0.04;

let material: THREE.Material | null = null;
const geometries = new Map<number, THREE.BufferGeometry>();

function ringMaterial(): THREE.Material {
  if (material === null) {
    material = new THREE.MeshBasicMaterial({
      color: NODE_RING_COLOUR,
      transparent: true,
      opacity: 0.6,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
  }
  return material;
}

function ringGeometry(radius: number): THREE.BufferGeometry {
  let geometry = geometries.get(radius);
  if (geometry === undefined) {
    geometry = new THREE.RingGeometry(radius - RING_WIDTH, radius, 40);
    geometries.set(radius, geometry);
  }
  return geometry;
}

/** Build one ground ring for a node of `archetype` (its origin is the node's ground point). */
export function createNodeHighlight(archetype: string | null): THREE.Object3D {
  const radius =
    NODE_RING_RADIUS[archetype ?? 'default'] ?? (NODE_RING_RADIUS['default'] as number);
  const ring = new THREE.Mesh(ringGeometry(radius), ringMaterial());
  ring.rotation.x = -Math.PI / 2;
  ring.renderOrder = 1;
  const group = new THREE.Group();
  group.name = 'node-highlight';
  group.userData['ringRadius'] = radius; // the gather-progress fill sizes itself from it
  ring.position.y = RING_LIFT;
  group.add(ring);
  return group;
}

// ---------------------------------------------------------------------------
// Gather-channel progress on the ring (AD-665 C4, owner 2026-09-25): the ring's
// centre fills INWARD — a band whose inner hole shrinks to nothing until the ring is
// a full disc — so it reads the same from every camera angle. Geometry is swapped
// between quantised steps (cached), so it works on both renderers with no shader.
// ---------------------------------------------------------------------------

/** Fill steps (one geometry each per ring size); 24 reads smooth over a 2 s channel. */
export const RING_FILL_STEPS = 24;

let fillMaterial: THREE.Material | null = null;
const fillGeometries = new Map<string, THREE.BufferGeometry>();

function ringFillMaterial(): THREE.Material {
  if (fillMaterial === null) {
    fillMaterial = new THREE.MeshBasicMaterial({
      color: NODE_RING_COLOUR,
      transparent: true,
      opacity: 0.45,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
  }
  return fillMaterial;
}

/** The inner-hole radius for fill fraction `frac` inside an outline of `radius` (pure). */
export function ringFillInnerRadius(radius: number, frac: number): number {
  const outer = radius - RING_WIDTH;
  const f = Math.max(0, Math.min(1, frac));
  const step = Math.round(f * RING_FILL_STEPS) / RING_FILL_STEPS;
  return outer * (1 - step);
}

function fillGeometry(radius: number, inner: number): THREE.BufferGeometry {
  const key = `${radius}:${inner.toFixed(4)}`;
  let geometry = fillGeometries.get(key);
  if (geometry === undefined) {
    geometry =
      inner <= 1e-4
        ? new THREE.CircleGeometry(radius - RING_WIDTH, 40)
        : new THREE.RingGeometry(inner, radius - RING_WIDTH, 40);
    fillGeometries.set(key, geometry);
  }
  return geometry;
}

/**
 * Show a gather channel's progress on a node's ring: `frac` 0..1 fills the inside inward;
 * `null` removes the fill (channel over). The outline stays throughout.
 */
export function setNodeHighlightProgress(highlight: THREE.Object3D, frac: number | null): void {
  let fill = highlight.getObjectByName('node-ring-fill') as THREE.Mesh | undefined;
  if (frac === null) {
    if (fill) highlight.remove(fill);
    return;
  }
  const radius =
    (highlight.userData['ringRadius'] as number | undefined) ?? NODE_RING_RADIUS['default']!;
  const inner = ringFillInnerRadius(radius, frac);
  const geometry = fillGeometry(radius, inner);
  if (fill === undefined) {
    fill = new THREE.Mesh(geometry, ringFillMaterial());
    fill.name = 'node-ring-fill';
    fill.rotation.x = -Math.PI / 2;
    fill.position.y = RING_LIFT;
    fill.renderOrder = 1;
    highlight.add(fill);
  } else if (fill.geometry !== geometry) {
    fill.geometry = geometry;
  }
}
